import * as React from "react";
import { FolderKanban, Plus, Search, Filter } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

/**
 * ProjectsPage - Project management
 *
 * Features:
 * - Search and filter toolbar
 * - Project cards with progress indicators
 */
export function ProjectsPage() {
  const { t } = useTranslation("dashboard");

  const placeholderProjects = [
    { name: "Website Redesign", status: "In Progress", progress: 65 },
    { name: "Mobile App", status: "Planning", progress: 20 },
    { name: "API Integration", status: "Review", progress: 90 },
    { name: "Documentation", status: "In Progress", progress: 45 },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("pages.projects.title")}
          </h1>
          <p className="text-base text-muted-foreground md:text-lg">
            {t("pages.projects.description")}
          </p>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      <Separator />

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            className="border-border bg-muted pl-9 text-sm placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <Button variant="outline">
          <Filter className="mr-2 h-4 w-4" />
          Filter
        </Button>
      </div>

      {/* Project cards */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          All Projects
        </h2>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {placeholderProjects.map((project) => (
            <Card key={project.name}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
                    <FolderKanban className="h-5 w-5 text-primary" />
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      project.status === "In Progress"
                        ? "bg-primary/10 text-primary"
                        : project.status === "Review"
                          ? "bg-amber-500/10 text-amber-600"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {project.status}
                  </span>
                </div>
                <h3 className="mt-4 font-medium text-foreground">{project.name}</h3>
                <div className="mt-3">
                  <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Progress</span>
                    <span>{project.progress}%</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

export default ProjectsPage;
